import React, { FC, useState, useRef, useEffect } from 'react';
import { Button, Modal } from 'flowbite-react';
import { t } from 'i18next';
import { HiOutlineCloudArrowUp } from 'react-icons/hi2';
import { useCreateMediaMutation } from '../../redux/api/mediaApi';
import { getMediaType } from '../../utils/getMediaType';
import { FilePreview } from '../Shared/FilePreview';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const AddMediaModal: FC<Props> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [customName, setCustomName] = useState('');
  const [createMedia, { isLoading: isUploading }] = useCreateMediaMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (file) {
      setCustomName(file.name);
    } else {
      setCustomName('');
    }
  }, [file]);

  const handleUpload = async () => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      // Mapping types
      const mediaType = getMediaType(file.type);
      if (mediaType === 'UNKNOWN') {
        return;
      }
      formData.append('mediaType', mediaType);
      if (customName && customName !== file.name) {
        formData.append('name', customName);
      }
      try {
        // @ts-expect-error createMedia is typed for a DTO; FormData is forwarded to the RTK Query endpoint
        await createMedia(formData).unwrap();
        setFile(null);
        onClose();
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
  };

  const handleCancelFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReplaceFile = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal show={isOpen} size="4xl" onClose={onClose}>
      <Modal.Header className="modal-header">
        <div className="flex-1">{t('media.addMedia')}</div>
      </Modal.Header>
      <Modal.Body>
        <div>
          {!file ? (
            <label
              htmlFor="file-upload"
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-10 transition-colors hover:border-primary-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-primary-500 dark:hover:bg-gray-700"
            >
              <HiOutlineCloudArrowUp className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-500" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('media.chooseFile')}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                PNG, JPG, MP4, MP3, PDF
              </p>
            </label>
          ) : (
            <FilePreview
              file={file}
              onRemove={handleCancelFile}
              onReplace={handleReplaceFile}
              onNameChange={setCustomName}
            />
          )}
          <input
            ref={fileInputRef}
            id="file-upload"
            type="file"
            accept="image/*,video/*,audio/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
          color="purple"
          className="me-4"
        >
          {isUploading ? t('media.uploading') : t('media.upload')}
        </Button>
        <Button onClick={onClose} color="light">
          {t('media.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddMediaModal;
